// src/apiConfig.ts
export interface AppConfig {
  API_BASE_URL: string;
  API_VIRTUAL_DIRECTORY_URL: string;
  RAZORPAY_KEY?: string;
  RAZORPAY_URL?: string;
  GOOGLE_CLIENT_ID?: string;
}

export class ApiConfig {
  private static instance: ApiConfig;
  private config: AppConfig | null = null;

  private constructor() {}

  // Singleton
  public static getInstance(): ApiConfig {
    if (!ApiConfig.instance) {
      ApiConfig.instance = new ApiConfig();
    }
    return ApiConfig.instance;
  }

  // Load environment variables directly
  public load(): void {
    this.config = {
      API_BASE_URL: process.env.REACT_APP_API_BASE_URL || '',
      API_VIRTUAL_DIRECTORY_URL: process.env.REACT_APP_API_VIRTUAL_DIRECTORY_URL || '',
      RAZORPAY_KEY: process.env.REACT_APP_RAZORPAY_KEY,
      RAZORPAY_URL: process.env.REACT_APP_RAZORPAY_URL,
      GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID
    };

    // Basic validation
    if (!this.config.API_BASE_URL || !this.config.API_VIRTUAL_DIRECTORY_URL) {
      throw new Error(
        "Missing API_BASE_URL or API_VIRTUAL_DIRECTORY_URL in environment variables"
      );
    }

    this.config = Object.freeze(this.config);
  }

  // Internal guard
  private ensure(): AppConfig {
    if (!this.config) {
      throw new Error(
        "[ApiConfig] Configuration not loaded. Call load() first."
      );
    }
    return this.config;
  }

  // ---- Getters ----
  public get apiUrl(): string {
    const { API_BASE_URL } = this.ensure();
    return API_BASE_URL.replace(/\/+$/, "");
  }

  // Keep original name for compatibility with existing code
  public get VirtualdirectoryUrl(): string {
    const { API_VIRTUAL_DIRECTORY_URL } = this.ensure();
    return API_VIRTUAL_DIRECTORY_URL.replace(/\/+$/, "");
  }

  public get razorpayKey(): string {
    const { RAZORPAY_KEY } = this.ensure();
    return (RAZORPAY_KEY || "").trim();
  }

  public get razorpayUrl(): string {
    const { RAZORPAY_URL } = this.ensure();
    // default to official CDN if not provided
    return (
      RAZORPAY_URL || "https://checkout.razorpay.com/v1/checkout.js"
    ).trim();
  }

  public get googleClientId(): string {
    if (!this.config) throw new Error("[ApiConfig] Call load() first.");
    const v = this.config.GOOGLE_CLIENT_ID || "";
    if (!v)
      throw new Error("[ApiConfig] GOOGLE_CLIENT_ID missing in environment variables");
    return v;
  }
}
